import React from 'react';
import { Container, Row, Col, Card, Alert, ListGroup, Badge } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import LearnerNavbar from '../components/LearnerNavbar';
import FacilitatorNavbar from '../components/FacilitatorNavbar';
import AdminNavbar from '../components/AdminNavbar';
import ExternalNavbar from '../components/ExternalNavbar';
import QANavbar from '../components/QANavbar';
import '../styles/brand.css';

const SupportPage = () => {
  const { userProfile } = useAuth();

  // Render appropriate navbar based on user role
  const renderNavbar = () => {
    switch (userProfile?.role) {
      case 'admin':
        return <AdminNavbar />;
      case 'facilitator':
        return <FacilitatorNavbar />;
      case 'qa':
        return <QANavbar />;
      case 'external':
      case 'external_facilitator':
      case 'external_facilitator_limited':
      case 'external_viewer':
      case 'external_viewer_reporting':
        return <ExternalNavbar />;
      default:
        return <LearnerNavbar />;
    }
  };

  // Support team contacts
  const supportTeam = [
    {
      name: 'NPI Programme Support',
      role: 'Programme Coordinator',
      email: 'support@npigovernance.co.za',
      phone: '+27 82 XXX XXXX',
      availability: 'Mon-Fri, 08:00-17:00',
      responsibilities: ['Programme queries', 'Schedule changes', 'General coordination']
    },
    {
      name: 'NPI Learner Support',
      role: 'Student Support Specialist',
      email: 'support@npigovernance.co.za',
      phone: '+27 82 XXX XXXX',
      availability: 'Mon-Fri, 08:00-17:00',
      responsibilities: ['Learner queries', 'Onboarding assistance', 'Document support']
    },
    {
      name: 'NPI Technical Support',
      role: 'Technical Support',
      email: 'support@npigovernance.co.za',
      phone: '+27 82 XXX XXXX',
      availability: 'Mon-Fri, 08:00-17:00',
      responsibilities: ['Technical issues', 'Login problems', 'Platform assistance']
    }
  ];

  return (
    <>
      {renderNavbar()}

      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row>
            <Col lg={8}>
              <h1 className="mb-3">Help & Support</h1>
              <p className="mission-text mb-4">
                We're here to help you succeed in your learning journey
              </p>
              <p className="mb-0">
                Contact our support team for assistance with any questions or issues you may have.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      <Container className="mt-4 pb-5">
        {/* Primary Support Email */}
        <Row className="mb-4">
          <Col>
            <Card className="border-primary">
              <Card.Header className="bg-primary text-white">
                <h5 className="mb-0">Primary Support Contact</h5>
              </Card.Header>
              <Card.Body className="text-center">
                <h4>
                  <a href="mailto:support@npigovernance.co.za" className="text-decoration-none">
                    support@npigovernance.co.za
                  </a>
                </h4>
                <p className="text-muted mb-0">
                  For all general enquiries and support requests, please email our main support address.
                  We aim to respond within 24 business hours.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Support Team Cards */}
        <Row className="mb-4">
          <Col>
            <h4 className="mb-3">Our Support Team</h4>
          </Col>
        </Row>
        <Row>
          {supportTeam.map((member, index) => (
            <Col md={4} key={index} className="mb-4">
              <Card className="h-100">
                <Card.Header>
                  <strong>{member.name}</strong>
                  <br />
                  <Badge bg="secondary" className="mt-1">{member.role}</Badge>
                </Card.Header>
                <Card.Body>
                  <div className="mb-3">
                    <strong>Email:</strong>
                    <br />
                    <a href={`mailto:${member.email}`}>{member.email}</a>
                  </div>
                  <div className="mb-3">
                    <strong>Availability:</strong>
                    <br />
                    <small className="text-muted">{member.availability}</small>
                  </div>
                  <div>
                    <strong>Can help with:</strong>
                    <ListGroup variant="flush" className="mt-1">
                      {member.responsibilities.map((resp, idx) => (
                        <ListGroup.Item key={idx} className="py-1 px-0 border-0">
                          <small>- {resp}</small>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Quick Help Section */}
        <Row className="mb-4">
          <Col>
            <Card>
              <Card.Header>
                <h5 className="mb-0">Frequently Asked Questions</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <h6>Login Issues</h6>
                    <p className="text-muted small">
                      If you're having trouble logging in, try resetting your password using the "Forgot Password" link
                      on the login page. If the issue persists, contact Itumeleng for technical support.
                    </p>

                    <h6>Document Uploads</h6>
                    <p className="text-muted small">
                      Ensure your documents are in PDF, JPG, or PNG format and under 10MB.
                      If you encounter upload errors, try using a different browser or contact Bongeka.
                    </p>
                  </Col>
                  <Col md={6}>
                    <h6>Schedule Changes</h6>
                    <p className="text-muted small">
                      For any changes to your training schedule or attendance issues,
                      please contact Jecholias as soon as possible.
                    </p>

                    <h6>Assessment Queries</h6>
                    <p className="text-muted small">
                      Questions about assignments, grades, or assessments should be directed to your facilitator
                      or contact the support email for routing to the appropriate person.
                    </p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Emergency Contact */}
        <Row>
          <Col>
            <Alert variant="info">
              <Alert.Heading>Need Urgent Help?</Alert.Heading>
              <p className="mb-0">
                For urgent matters outside of business hours, please send an email to{' '}
                <a href="mailto:support@npigovernance.co.za">support@npigovernance.co.za</a>{' '}
                with "URGENT" in the subject line. We monitor urgent requests and will respond as soon as possible.
              </p>
            </Alert>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default SupportPage;
